import EditableList from '@/components/EditableList'
import ProfileRow from '@/components/Profile/ProfileRow'
import { Button } from '@/components/ui/button'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import * as kinds from '@nostr/tools/kinds'
import { username } from '@/lib/event-metadata'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { mutePrivately, mutePublicly } from '@/atoms/mutes'
import { useAtomValue } from 'jotai'
import { invalidateMuteList, muteListChannelAtom } from '@/atoms/invalidation'
import { unwrappedSignerAtom, currentPubkeyAtom } from '@/atoms/session'
import client from '@/services/client.service'
import { loadMuteList } from '@nostr/gadgets/lists'
import * as nip19 from '@nostr/tools/nip19'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  LockIcon,
  Loading03Icon,
  AlertCircleIcon,
  CircleUnlock01Icon
} from '@hugeicons/core-free-icons'
import { forwardRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAsyncRetry } from 'react-use'
import { now } from '@/lib/utils'
import { sampleTopFromFollows } from '@/lib/recommendations'
import { unwrappedMuteListAtom } from '@/atoms/lists'

const MuteListPage = forwardRef(({ id, index }: { id?: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const signer = useAtomValue(unwrappedSignerAtom)
  const currentPubkey = useAtomValue(currentPubkeyAtom)
  const { profile } = useFetchProfile(id)
  const channel = useAtomValue(muteListChannelAtom)
  const ourMuteList = useAtomValue(unwrappedMuteListAtom)

  useEffect(() => {
    if (!profile) return
    const name = profile.shortName || username(profile) || '...'
    document.title = t("{{name}}'s mutes on {{appName}}", {
      name,
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])
  const { value: muteList } = useAsyncRetry(async () => {
    if (!profile) return
    if (profile.pubkey === currentPubkey)
      return { list: ourMuteList ?? { public: [], private: [] }, event: null }
    return client.fetchMuteList(profile.pubkey)
  }, [profile?.pubkey, channel, ourMuteList])
  if (!profile) return

  async function loadRecommendations(): Promise<string[]> {
    if (!currentPubkey || !muteList) return []
    const exclude = new Set([...muteList.list.public, ...muteList.list.private, currentPubkey])
    const fromFollows = await sampleTopFromFollows<string>(
      currentPubkey,
      async (pk) => {
        const items = (await loadMuteList(pk)).items
        return items.filter((i) => i.label === 'pubkey').map((i) => i.value)
      },
      (pk) => pk,
      exclude
    )
    return fromFollows
  }

  const isSelf = profile?.pubkey === currentPubkey

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      title={t("{{username}}'s muted", { username: username(profile) })}
    >
      <div className="px-4 py-3 space-y-4">
        {isSelf && !signer?.nip04Decrypt && (
          <div className="flex items-start gap-2 text-sm text-amber-500 border border-amber-500/30 rounded-lg p-3">
            <HugeiconsIcon icon={AlertCircleIcon} className="size-4 shrink-0 mt-0.5" />
            <span>
              {t('Your login method does not support encryption. Private muting is unavailable.')}
            </span>
          </div>
        )}

        {isSelf ? (
          <EditableList
            items={muteList ? muteList.list.public.concat(muteList.list.private) : []}
            editable={isSelf}
            renderItem={(pubkey) => <MuteRow pubkey={pubkey} />}
            validateItem={(val) => {
              let hex: string
              if (val.startsWith('npub') || val.startsWith('nprofile')) {
                const decoded = nip19.decode(val)
                hex = decoded.data as string
              } else if (/^[0-9a-f]{64}$/i.test(val)) {
                hex = val
              } else {
                return { ok: false as const, error: t('Invalid pubkey') }
              }
              return { ok: true as const, item: hex }
            }}
            onUpdate={async (newPubkeys: string[]) => {
              if (!currentPubkey || !muteList) return

              const existingType = new Map<string, 'public' | 'private'>()
              for (const pk of muteList.list.public) existingType.set(pk, 'public')
              for (const pk of muteList.list.private) existingType.set(pk, 'private')

              const newPublic: string[] = []
              const newPrivate: string[] = []

              for (const pk of newPubkeys) {
                const type = existingType.get(pk)
                if (type === 'private') {
                  newPrivate.push(pk)
                } else {
                  newPublic.push(pk)
                }
              }

              const tags = newPublic.map((pk) => ['p', pk])
              const content = signer?.nip04Encrypt
                ? await signer.nip04Encrypt(
                    currentPubkey,
                    JSON.stringify(newPrivate.map((pk) => ['p', pk]))
                  )
                : ''

              const event = await client.publish({
                kind: kinds.Mutelist,
                content: content ?? '',
                created_at: now(),
                tags
              })
              if (!event) {
                console.warn('failed to publish updated mute list event')
                toast.error(t('Failed to publish updated mute list event.'))
                return
              }
              await loadMuteList(event.pubkey, undefined, event)
              invalidateMuteList()
            }}
            placeholder={t('Enter npub or hex')}
            emptyMessage={t('No muted users.')}
            loadRecommendations={loadRecommendations}
          />
        ) : (
          <EditableList
            items={muteList?.list.public || []}
            editable={false}
            renderItem={(pubkey) => <ProfileRow pubkey={pubkey} showAbout />}
            validateItem={() => ({ ok: false as const, error: '' })}
            onUpdate={async () => {}}
            emptyMessage={t('No muted users')}
          />
        )}
      </div>
    </SecondaryPageLayout>
  )
})

MuteListPage.displayName = 'MuteListPage'
export default MuteListPage

function MuteRow({ pubkey }: { pubkey: string }) {
  const m = useAtomValue(unwrappedMuteListAtom)
  const muteType = m?.public.includes(pubkey) ? 'public' : 'private'

  const [switching, setSwitching] = useState(false)

  return (
    <ProfileRow
      pubkey={pubkey}
      showAbout
      actions={
        <>
          {muteType && (
            <Button variant="ghost" size="icon" onClick={handleToggle} disabled={switching}>
              {switching ? (
                <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
              ) : muteType === 'private' ? (
                <HugeiconsIcon icon={LockIcon} className="text-green-400" />
              ) : (
                <HugeiconsIcon icon={CircleUnlock01Icon} className="text-muted-foreground" />
              )}
            </Button>
          )}
        </>
      }
    />
  )

  async function handleToggle() {
    if (switching) return
    setSwitching(true)
    try {
      if (muteType === 'private') {
        await mutePublicly(pubkey)
      } else {
        await mutePrivately(pubkey)
      }
    } finally {
      setSwitching(false)
    }
  }
}
